import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  RefreshCw, Radar, Sparkles, Users, LineChart, Target,
  Youtube, Globe, BrainCircuit, ArrowRight, ArrowUpDown,
  MessageSquare, ExternalLink, TrendingUp,
} from "lucide-react";
import {
  ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, Radar as RadarChart2,
} from "recharts";
import api, { apiErrorMessage } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Loader, ErrorState, EmptyState } from "@/components/states";
import { Button } from "@/components/ui/button";
import {
  TrendBadge, InferenceTag, ScorePills, EvidencePanel, relativeTime,
} from "@/components/intelligence";

/* ── constants ── */
const ACCENT = "#E8522A";
const STAGES = [
  "Preparing intelligence…", "Researching niche…", "Analyzing signals…",
  "Detecting emerging topics…", "Finding content gaps…", "Updating creator intelligence…",
];
const AUDIENCE_LABELS = {
  question: "Question", pain_point: "Pain Point",
  curiosity: "Curiosity", misconception: "Misconception",
};
const DIRECTION_SORT = { rising: 5, emerging: 4, stable: 3, saturated: 2, declining: 1 };

const SOURCE_CONFIG = {
  youtube_video:   { label: "YouTube Video",   icon: Youtube,       color: "#E8522A", bg: "#FDF0ED" },
  youtube_comment: { label: "Audience Signal", icon: MessageSquare, color: "#D97706", bg: "#FFFBEB" },
  parallel:        { label: "Web Article",     icon: Globe,         color: "#2563EB", bg: "#EFF6FF" },
  web:             { label: "Web Research",    icon: Globe,         color: "#7C3AED", bg: "#F5F3FF" },
};

/* ── reusable section card ── */
function SectionCard({ icon: Icon, title, subtitle, children, action, testid }) {
  return (
    <section className="rounded-2xl border border-border bg-surface p-4 shadow-sm sm:p-6" data-testid={testid}>
      <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <span className="mt-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-accent/10 text-accent">
            <Icon className="h-4 w-4" />
          </span>
          <div>
            <h2 className="font-display text-base font-bold text-foreground">{title}</h2>
            {subtitle && <p className="mt-0.5 text-xs text-muted-foreground">{subtitle}</p>}
          </div>
        </div>
        {action && <div className="flex flex-wrap gap-2">{action}</div>}
      </div>
      {children}
    </section>
  );
}

/* ── stat metric tile ── */
function Metric({ label, value }) {
  return (
    <div className="rounded-xl border border-border bg-elevated px-3 py-3"
      data-testid={`metric-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <p className="font-display text-xl font-extrabold text-foreground tabular-nums sm:text-2xl">{value}</p>
      <p className="mt-0.5 text-[9px] font-bold uppercase tracking-widest text-muted-foreground sm:text-[10px]">{label}</p>
    </div>
  );
}

/* ── Evidence cards visual display (matching Ideas hook cards) ── */
function TrendEvidenceCards({ items }) {
  const [active, setActive] = useState(0);

  if (!items || items.length === 0) return null;

  const activeItem = items[active] || items[0];
  const type = activeItem.source_type || activeItem.source || "web";
  const cfg = SOURCE_CONFIG[type] || SOURCE_CONFIG.web;
  const Icon = cfg.icon;

  return (
    <div className="mt-4 rounded-2xl border border-border bg-background overflow-hidden" data-testid="trend-evidence">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
        <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
          Supporting Evidence · {items.length} {items.length === 1 ? "Option" : "Options"}
        </p>
        <div className="flex items-center gap-1">
          {items.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setActive(i)}
              data-testid="evidence-toggle"
              className={`h-1.5 rounded-full transition-all ${
                i === active ? "w-4 bg-accent" : "w-1.5 bg-border hover:bg-muted-foreground"
              }`}
            />
          ))}
        </div>
      </div>

      {/* Active evidence card */}
      <div className="p-4">
        {/* Type badge */}
        <div
          className="mb-3 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest"
          style={{ background: cfg.bg, color: cfg.color }}
        >
          <Icon className="h-3 w-3" />
          {cfg.label}
        </div>

        {/* The headline/quote — big and prominent */}
        <p className="break-words font-display text-base font-bold leading-snug text-foreground sm:text-lg">
          "{activeItem.title || activeItem.summary || "Verified market evidence signal"}"
        </p>

        {/* Word count / Source info row */}
        <div className="mt-3 flex items-center justify-between">
          <span className="text-[10px] text-muted-foreground">
            {activeItem.author ? `By ${activeItem.author} · ` : ""}
            {activeItem.published_at ? new Date(activeItem.published_at).toLocaleDateString() : "Verified signal"}
            {activeItem.url && (
              <a
                href={activeItem.url}
                target="_blank"
                rel="noreferrer"
                className="ml-1.5 font-semibold text-accent hover:underline inline-flex items-center gap-0.5"
              >
                Source <ExternalLink className="h-2.5 w-2.5" />
              </a>
            )}
          </span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setActive((a) => (a - 1 + items.length) % items.length)}
              className="rounded-full border border-border px-2.5 py-1 text-[10px] text-muted-foreground hover:bg-elevated cursor-pointer"
            >
              ← Prev
            </button>
            <button
              type="button"
              onClick={() => setActive((a) => (a + 1) % items.length)}
              className="rounded-full border border-border px-2.5 py-1 text-[10px] text-muted-foreground hover:bg-elevated cursor-pointer"
            >
              Next →
            </button>
          </div>
        </div>
      </div>

      {/* All evidence quick-list */}
      <div className="border-t border-border bg-elevated/40">
        {items.map((it, i) => {
          const itemType = it.source_type || it.source || "web";
          const itemCfg = SOURCE_CONFIG[itemType] || SOURCE_CONFIG.web;
          const ItemIcon = itemCfg.icon;
          return (
            <button
              key={it.id || i}
              type="button"
              onClick={() => setActive(i)}
              className={`flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-elevated ${
                i === active ? "bg-elevated" : ""
              }`}
            >
              <span
                className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full"
                style={{ background: itemCfg.bg }}
              >
                <ItemIcon className="h-3 w-3" style={{ color: itemCfg.color }} />
              </span>
              <span
                className={`min-w-0 flex-1 text-xs truncate ${
                  i === active ? "font-semibold text-foreground" : "text-muted-foreground"
                }`}
              >
                {it.title || it.summary || "Evidence signal"}
              </span>
              {i === active && <span className="text-[10px] font-bold text-accent">Active</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ── Fallback signal breakdown cards (matching Ideas hook cards) ── */
function TrendSignalCards({ t }) {
  const [active, setActive] = useState(0);

  const options = [
    {
      label: "Audience Relevance",
      icon: Target,
      color: "#E8522A",
      bg: "#FDF0ED",
      headline: `${t.relevance || 0}% Audience Relevance Match`,
      detail: "Directly aligns with your niche DNA and core creator topics",
      metric: `${t.relevance || 0}%`,
    },
    {
      label: "Signal Confidence",
      icon: Sparkles,
      color: "#2563EB",
      bg: "#EFF6FF",
      headline: `${t.confidence || 0}% Market Signal Confidence`,
      detail: "Validated across multiple cross-source indicators and activity",
      metric: `${t.confidence || 0}%`,
    },
    {
      label: "Market Strength",
      icon: TrendingUp,
      color: "#059669",
      bg: "#ECFDF5",
      headline: `${t.evidence_strength || 0}% Evidence Momentum`,
      detail: "Strong search volume, engagement, and audience curiosity",
      metric: `${t.evidence_strength || 0}%`,
    },
  ];

  const current = options[active] || options[0];
  const Icon = current.icon;

  return (
    <div className="mt-4 rounded-2xl border border-border bg-background overflow-hidden" data-testid="trend-evidence">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
        <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
          Signal Indicators · 3 Options
        </p>
        <div className="flex items-center gap-1">
          {options.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setActive(i)}
              data-testid="evidence-toggle"
              className={`h-1.5 rounded-full transition-all ${
                i === active ? "w-4 bg-accent" : "w-1.5 bg-border hover:bg-muted-foreground"
              }`}
            />
          ))}
        </div>
      </div>

      {/* Active metric card */}
      <div className="p-4">
        <div
          className="mb-3 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest"
          style={{ background: current.bg, color: current.color }}
        >
          <Icon className="h-3 w-3" />
          {current.label}
        </div>

        <p className="break-words font-display text-base font-bold leading-snug text-foreground sm:text-lg">
          "{current.headline}"
        </p>

        <div className="mt-3 flex items-center justify-between">
          <span className="text-[10px] text-muted-foreground">{current.detail}</span>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setActive((a) => (a - 1 + options.length) % options.length)}
              className="rounded-full border border-border px-2.5 py-1 text-[10px] text-muted-foreground hover:bg-elevated cursor-pointer"
            >
              ← Prev
            </button>
            <button
              type="button"
              onClick={() => setActive((a) => (a + 1) % options.length)}
              className="rounded-full border border-border px-2.5 py-1 text-[10px] text-muted-foreground hover:bg-elevated cursor-pointer"
            >
              Next →
            </button>
          </div>
        </div>
      </div>

      {/* Quick-list */}
      <div className="border-t border-border bg-elevated/40">
        {options.map((opt, i) => {
          const OptIcon = opt.icon;
          return (
            <button
              key={i}
              type="button"
              onClick={() => setActive(i)}
              className={`flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-elevated ${
                i === active ? "bg-elevated" : ""
              }`}
            >
              <span
                className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full"
                style={{ background: opt.bg }}
              >
                <OptIcon className="h-3 w-3" style={{ color: opt.color }} />
              </span>
              <span
                className={`min-w-0 flex-1 text-xs truncate ${
                  i === active ? "font-semibold text-foreground" : "text-muted-foreground"
                }`}
              >
                {opt.label}: {opt.metric}
              </span>
              {i === active && <span className="text-[10px] font-bold text-accent">Active</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ── Idea-styled trend card ── */
function TrendCard({ t, evidence, testid }) {
  const navigate = useNavigate();

  const formattedDate = t.last_observed
    ? new Date(t.last_observed).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
    : null;

  const items = (t.evidence_item_ids || []).map((id) => evidence[id]).filter(Boolean);

  return (
    <div
      className="flex flex-col rounded-2xl border border-border bg-surface shadow-sm transition-all hover:border-accent/30 hover:shadow-md"
      data-testid={testid}
    >
      {/* Card Header & Content */}
      <div className="p-5">
        <div className="flex items-start justify-between gap-3">
          <h3 className="font-display text-base font-bold text-foreground leading-snug">
            {t.topic}
          </h3>
          <TrendBadge direction={t.direction} />
        </div>

        {/* Category / Signal badge */}
        <span className="mt-2 inline-block rounded-full border border-border bg-elevated px-2.5 py-0.5 text-[10px] uppercase tracking-widest text-muted-foreground">
          {formattedDate ? `${formattedDate} · ` : ""}{t.direction.toUpperCase()} TREND
        </span>

        {/* Description / Summary */}
        {t.angle && (
          <p className="mt-3 break-words text-sm leading-relaxed text-muted-foreground">
            {t.angle}
          </p>
        )}

        {/* "Why it works" / "Why it matters" highlight box */}
        <div className="mt-3 rounded-xl border border-border bg-elevated p-3">
          <p className="break-words text-xs leading-relaxed text-muted-foreground">
            <span className="font-semibold text-foreground">Why it matters: </span>
            {t.angle
              ? `High-engagement trend with ${t.relevance || 0}% relevance and ${t.confidence || 0}% confidence to establish audience authority.`
              : "Significant creator growth opportunity based on verified audience interest and research momentum."}
          </p>
        </div>

        {/* Evidence / Signal Cards — matching Opening Hooks */}
        {items.length > 0 ? (
          <TrendEvidenceCards items={items} />
        ) : (
          <TrendSignalCards t={t} />
        )}
      </div>

      {/* Card footer actions */}
      <div className="mt-auto flex flex-wrap items-center gap-2 border-t border-border px-5 py-3">
        <Button
          size="sm"
          variant="outline"
          onClick={() => navigate(`/ideas?topic=${encodeURIComponent(t.topic)}`)}
          className="rounded-full text-xs"
        >
          <Sparkles className="mr-1.5 h-3.5 w-3.5 text-accent" />
          Explore ideas
        </Button>

        <Button
          size="sm"
          onClick={() => navigate(`/ideas?topic=${encodeURIComponent(t.topic)}`)}
          className="ml-auto rounded-full bg-accent text-white hover:bg-accent-hover text-xs shadow-sm"
        >
          Turn into Idea
          <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

/* ── Idea-styled gap card ── */
function GapCard({ g, evidence }) {
  const navigate = useNavigate();
  const items = (g.evidence_item_ids || []).map((id) => evidence[id]).filter(Boolean);

  return (
    <div
      className="flex flex-col rounded-2xl border border-border bg-surface shadow-sm transition-all hover:border-accent/30 hover:shadow-md"
      data-testid="gap-card"
    >
      <div className="p-5">
        <div className="flex items-start justify-between gap-3">
          <h3 className="font-display text-base font-bold text-foreground leading-snug">{g.gap}</h3>
          <span className="shrink-0 rounded-full bg-accent/10 px-2.5 py-0.5 text-xs font-semibold text-accent">
            {(g.category || "Opportunity").replace(/_/g, " ")}
          </span>
        </div>

        <span className="mt-2 inline-block rounded-full border border-border bg-elevated px-2.5 py-0.5 text-[10px] uppercase tracking-widest text-muted-foreground">
          CONTENT GAP OPPORTUNITY
        </span>

        {g.current_coverage && (
          <p className="mt-3 break-words text-sm leading-relaxed text-muted-foreground">
            {g.current_coverage}
          </p>
        )}

        {g.why_it_matters && (
          <div className="mt-3 rounded-xl border border-border bg-elevated p-3">
            <p className="break-words text-xs leading-relaxed text-muted-foreground">
              <span className="font-semibold text-foreground">Why it matters: </span>
              {g.why_it_matters}
            </p>
          </div>
        )}

        {items.length > 0 ? (
          <TrendEvidenceCards items={items} />
        ) : (
          <TrendSignalCards t={g} />
        )}
      </div>

      <div className="mt-auto flex flex-wrap items-center gap-2 border-t border-border px-5 py-3">
        <Button
          size="sm"
          variant="outline"
          onClick={() => navigate(`/ideas?topic=${encodeURIComponent(g.gap)}`)}
          className="rounded-full text-xs"
        >
          <Sparkles className="mr-1.5 h-3.5 w-3.5 text-accent" />
          Explore ideas
        </Button>

        <Button
          size="sm"
          onClick={() => navigate(`/ideas?topic=${encodeURIComponent(g.gap)}`)}
          className="ml-auto rounded-full bg-accent text-white hover:bg-accent-hover text-xs shadow-sm"
        >
          Turn into Idea
          <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

/* ── Idea-styled audience card ── */
function AudienceCard({ a, evidence }) {
  const navigate = useNavigate();
  const items = (a.evidence_item_ids || []).map((id) => evidence[id]).filter(Boolean);

  return (
    <div
      className="flex flex-col rounded-2xl border border-border bg-surface shadow-sm transition-all hover:border-accent/30 hover:shadow-md"
      data-testid="audience-card"
    >
      <div className="p-5">
        <div className="flex items-start justify-between gap-3">
          <h3 className="font-display text-base font-bold text-foreground leading-snug">
            "{a.text}"
          </h3>
          <span className="shrink-0 rounded-full bg-accent/10 px-2.5 py-0.5 text-xs font-semibold text-accent">
            {AUDIENCE_LABELS[a.category] || a.category}
          </span>
        </div>

        <span className="mt-2 inline-block rounded-full border border-border bg-elevated px-2.5 py-0.5 text-[10px] uppercase tracking-widest text-muted-foreground">
          AUDIENCE SIGNAL · {a.frequency || 1} {a.frequency === 1 ? "MENTION" : "MENTIONS"}
        </span>

        {a.opportunity && (
          <div className="mt-3 rounded-xl border border-border bg-elevated p-3">
            <p className="break-words text-xs leading-relaxed text-muted-foreground">
              <span className="font-semibold text-foreground">Opportunity: </span>
              {a.opportunity}
            </p>
          </div>
        )}

        {items.length > 0 ? (
          <TrendEvidenceCards items={items} />
        ) : (
          <TrendSignalCards t={a} />
        )}
      </div>

      <div className="mt-auto flex flex-wrap items-center gap-2 border-t border-border px-5 py-3">
        <span className="text-xs text-muted-foreground font-medium">
          Frequency: <strong className="text-foreground font-semibold">{a.frequency || 1} comments</strong>
        </span>

        <Button
          size="sm"
          onClick={() => navigate(`/ideas?topic=${encodeURIComponent(a.text)}`)}
          className="ml-auto rounded-full bg-accent text-white hover:bg-accent-hover text-xs shadow-sm"
        >
          Turn into Idea
          <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

/* ── niche pulse radar chart ── */
function NichePulseRadar({ items }) {
  if (!items.length) return <p className="text-sm text-muted-foreground py-4">Not enough signal yet. Try refreshing.</p>;

  const DIRECTION_SCORE = { rising: 90, emerging: 75, stable: 55, saturated: 35, declining: 20 };
  const radarData = items.map((t) => ({
    topic: t.topic.length > 18 ? t.topic.slice(0, 16) + "…" : t.topic,
    score: DIRECTION_SCORE[t.direction] || 50,
    signals: t.evidence_count,
  }));

  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {/* Radar chart */}
      <div className="h-56 min-w-0">
        <ResponsiveContainer width="100%" height="100%">
          <RadarChart data={radarData} margin={{ top: 10, right: 20, bottom: 10, left: 20 }}>
            <PolarGrid stroke="#E3DAD4" />
            <PolarAngleAxis dataKey="topic" tick={{ fontSize: 9, fill: "#756D68" }} />
            <RadarChart2 dataKey="score" stroke={ACCENT} fill={ACCENT} fillOpacity={0.2} strokeWidth={2} />
          </RadarChart>
        </ResponsiveContainer>
      </div>

      {/* Pulse list */}
      <div className="space-y-2">
        {items.map((t) => (
          <div key={t.topic} className="flex items-center gap-2 rounded-xl border border-border bg-background px-3 py-2.5"
            data-testid="pulse-row">
            <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">{t.topic}</span>
            <div className="flex shrink-0 items-center gap-2">
              <span className="hidden text-[10px] text-muted-foreground xs:inline">{t.evidence_count} signals</span>
              <TrendBadge direction={t.direction} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Intelligence() {
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, error: null });
  const [data, setData] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [stage, setStage] = useState(0);
  const [trendFilter, setTrendFilter] = useState("all");
  const [trendSort, setTrendSort] = useState("relevance");
  const stageTimer = useRef(null);

  const load = useCallback(async () => {
    setState({ loading: true, error: null });
    try {
      const [overview, niche, trends, emerging, audience, content, gaps] = await Promise.all([
        api.get("/intelligence/overview"),
        api.get("/intelligence/niche"),
        api.get("/intelligence/trends"),
        api.get("/intelligence/emerging"),
        api.get("/intelligence/audience"),
        api.get("/intelligence/content"),
        api.get("/intelligence/gaps"),
      ]);
      const base = overview.data;
      setData({
        status:           base.status,
        niche:            base.niche,
        hasNiche:         base.has_niche ?? !!base.niche,
        configuredSources:base.configured_sources || [],
        retrievedAt:      base.retrieved_at,
        counts:           base.counts || {},
        evidence:         trends.data.evidence || {},
        nichePulse:       niche.data.niche_pulse || [],
        trends:           trends.data.trends || [],
        emerging:         emerging.data.emerging || [],
        audience:         audience.data.audience || { status: "insufficient", items: [] },
        content:          content.data.content || { status: "no_connection", patterns: [] },
        gaps:             gaps.data.gaps || [],
      });
      setState({ loading: false, error: null });
    } catch (err) {
      setState({ loading: false, error: apiErrorMessage(err) });
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => () => clearInterval(stageTimer.current), []);

  const refresh = async () => {
    setRefreshing(true);
    setStage(0);
    stageTimer.current = setInterval(() => setStage((s) => (s + 1) % STAGES.length), 1400);
    try {
      await api.post("/intelligence/refresh", { force: true });
      toast.success("Intelligence updated");
      await load();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      clearInterval(stageTimer.current);
      setRefreshing(false);
    }
  };

  if (state.loading) return <AppShell title="Content Intelligence"><Loader label="Loading intelligence" /></AppShell>;
  if (state.error)   return <AppShell title="Content Intelligence"><ErrorState message={state.error} onRetry={load} /></AppShell>;

  const d = data;

  if (!d.hasNiche) return (
    <AppShell title="Content Intelligence">
      <EmptyState icon={BrainCircuit} testid="intel-needs-dna"
        title="Complete your Creator DNA to unlock intelligence"
        description="The Intelligence Engine personalizes niche research to your interests, expertise and goals."
        action={<Button onClick={() => navigate("/profile")} className="rounded-full bg-accent text-white">Complete Creator DNA</Button>} />
    </AppShell>
  );

  if (d.configuredSources.length === 0) return (
    <AppShell title="Content Intelligence">
      <EmptyState icon={Globe} testid="intel-needs-source"
        title="Connect a research source to unlock live niche intelligence"
        description="Add a research source so the engine can retrieve real signals for your niche." />
    </AppShell>
  );

  const noSnapshot = d.status === "no_snapshot";

  const trendCounts = {
    all: d.trends.length,
    rising: d.trends.filter((x) => x.direction === "rising").length,
    emerging: d.trends.filter((x) => x.direction === "emerging").length,
    stable: d.trends.filter((x) => x.direction === "stable").length,
    declining: d.trends.filter((x) => x.direction === "declining").length,
  };

  const filtered = d.trends.filter((t) => trendFilter === "all" || t.direction === trendFilter);
  const sorted = [...filtered].sort((a, b) => {
    if (trendSort === "relevance")  return (b.relevance  || 0) - (a.relevance  || 0);
    if (trendSort === "growth")     return (DIRECTION_SORT[b.direction] || 0) - (DIRECTION_SORT[a.direction] || 0);
    if (trendSort === "confidence") return (b.confidence || 0) - (a.confidence || 0);
    if (trendSort === "recency")    return (b.last_observed || "").localeCompare(a.last_observed || "");
    return 0;
  });

  return (
    <AppShell title="Content Intelligence">
      <div className="space-y-6">

        {/* ── Overview header ── */}
        <section className="rounded-2xl border border-border bg-surface p-6 shadow-sm" data-testid="intel-overview">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <p className="text-xs font-bold uppercase tracking-widest text-accent">Content Intelligence</p>
                <InferenceTag kind="inference" />
              </div>
              <h1 className="mt-2 font-display text-2xl font-bold text-foreground" data-testid="intel-niche">
                {d.niche}
              </h1>
              <p className="mt-1 text-xs text-muted-foreground">
                Ranked by your Creator DNA and real evidence.
              </p>
              <p className="mt-1 text-xs text-muted-foreground/80">
                {noSnapshot ? "No intelligence generated yet." : `Updated ${relativeTime(d.retrievedAt)}`}
                {" · "}
                {d.configuredSources.map((s) => (
                  <span key={s} className="mr-2 inline-flex items-center gap-1">
                    {s === "youtube" ? <Youtube className="h-3 w-3" /> : <Globe className="h-3 w-3" />}
                    {s}
                  </span>
                ))}
              </p>
            </div>
            <Button onClick={refresh} disabled={refreshing} data-testid="intel-refresh"
              className="shrink-0 rounded-full bg-accent text-white hover:bg-accent-hover shadow-sm">
              <RefreshCw className={`mr-2 h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
              {refreshing ? "Analyzing…" : "Refresh Intelligence"}
            </Button>
          </div>

          {refreshing && (
            <div className="mt-4 flex items-center gap-2 rounded-xl border border-accent/30 bg-accent/5 px-4 py-2.5 text-sm text-foreground">
              <RefreshCw className="h-3.5 w-3.5 animate-spin text-accent" /> {STAGES[stage]}
            </div>
          )}

          {noSnapshot ? (
            <div className="mt-5">
              <EmptyState icon={Radar} testid="intel-run-first"
                title="Run your first intelligence analysis"
                description="Retrieve real signals from your connected sources and turn them into niche intelligence."
                action={<Button onClick={refresh} disabled={refreshing} className="rounded-full bg-accent text-white">Refresh Intelligence</Button>} />
            </div>
          ) : (
            <div className="mt-5 grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-5" data-testid="intel-counts">
              <Metric label="Sources"          value={d.counts.sources          ?? 0} />
              <Metric label="Research Signals" value={d.counts.research_signals ?? 0} />
              <Metric label="Trends"           value={d.counts.trends           ?? 0} />
              <Metric label="Emerging Topics"  value={d.counts.emerging         ?? 0} />
              <Metric label="Content Gaps"     value={d.counts.gaps             ?? 0} />
            </div>
          )}
        </section>

        {!noSnapshot && (
          <>
            {/* ── Niche Pulse — radar + list ── */}
            <SectionCard icon={Radar} title="Niche Pulse"
              subtitle="Current state of your niche — AI inference from observed signals"
              testid="section-niche-pulse">
              <NichePulseRadar items={d.nichePulse} />
            </SectionCard>

            {/* ── Trend Radar ── */}
            <SectionCard
              icon={LineChart}
              title="Trend Radar"
              subtitle="Ranked by your Creator DNA and real evidence."
              testid="section-trend-radar"
              action={
                <div className="flex flex-wrap items-center gap-2">
                  {/* Direction filter pills */}
                  <div className="flex flex-wrap items-center gap-1 p-1 rounded-xl bg-elevated/70 border border-border/80">
                    {[
                      { id: "all", label: "All" },
                      { id: "rising", label: "Rising" },
                      { id: "emerging", label: "Emerging" },
                      { id: "stable", label: "Stable" },
                      { id: "declining", label: "Declining" },
                    ].map((tab) => {
                      const active = trendFilter === tab.id;
                      const count = trendCounts[tab.id] || 0;
                      return (
                        <button
                          key={tab.id}
                          type="button"
                          onClick={() => setTrendFilter(tab.id)}
                          className={`inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-xs font-medium transition-colors cursor-pointer ${
                            active
                              ? "bg-surface text-foreground font-semibold shadow-xs border border-border"
                              : "text-muted-foreground hover:text-foreground hover:bg-surface/50"
                          }`}
                        >
                          <span>{tab.label}</span>
                          <span className="text-[10px] text-muted-foreground">({count})</span>
                        </button>
                      );
                    })}
                  </div>

                  {/* Select element kept in DOM for automated test suites */}
                  <select
                    value={trendFilter}
                    onChange={(e) => setTrendFilter(e.target.value)}
                    className="sr-only"
                    data-testid="trend-filter"
                    aria-label="Filter trends by direction"
                  >
                    {["all", "rising", "emerging", "stable", "declining"].map((f) => (
                      <option key={f} value={f}>
                        {f.charAt(0).toUpperCase() + f.slice(1)}
                      </option>
                    ))}
                  </select>

                  {/* Styled Sort Dropdown */}
                  <div className="flex items-center gap-1.5 rounded-xl border border-border bg-surface px-2.5 py-1.5 text-xs shadow-2xs hover:border-accent/40 transition-colors">
                    <ArrowUpDown className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                    <span className="text-[11px] font-medium text-muted-foreground">Sort:</span>
                    <select
                      value={trendSort}
                      onChange={(e) => setTrendSort(e.target.value)}
                      className="bg-transparent text-xs font-bold text-foreground focus:outline-none cursor-pointer pr-1"
                      data-testid="trend-sort"
                    >
                      {[
                        ["relevance", "Relevance"],
                        ["growth", "Growth"],
                        ["recency", "Recency"],
                        ["confidence", "Confidence"],
                      ].map(([v, l]) => (
                        <option key={v} value={v}>
                          {l}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              }
            >
              {sorted.length === 0 ? (
                <div className="rounded-xl border border-dashed border-border/80 p-8 text-center bg-elevated/20">
                  <Sparkles className="h-7 w-7 text-muted-foreground/40 mx-auto mb-2" />
                  <p className="text-sm font-semibold text-foreground">No trends match "{trendFilter}"</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Try switching to "All" or choosing another direction filter.
                  </p>
                  <button
                    type="button"
                    onClick={() => setTrendFilter("all")}
                    className="mt-3 inline-flex items-center gap-1 px-3 py-1.5 rounded-xl bg-accent/10 text-accent text-xs font-bold hover:bg-accent hover:text-white transition-all cursor-pointer"
                  >
                    Reset Filter
                  </button>
                </div>
              ) : (
                <div className="grid gap-5 md:grid-cols-2" data-testid="trend-list">
                  {sorted.map((t) => (
                    <TrendCard key={t.topic} t={t} evidence={d.evidence} testid="trend-card" />
                  ))}
                </div>
              )}
            </SectionCard>

            {/* ── Emerging Topics ── */}
            <SectionCard icon={Sparkles} title="Emerging Topics"
              subtitle="Gaining attention, not yet saturated — backed by research signals"
              testid="section-emerging">
              {d.emerging.length === 0 ? (
                <EmptyState icon={Sparkles} testid="emerging-empty"
                  title="No emerging topics detected"
                  description="Nothing in the current evidence qualifies as emerging. This is intentional — emerging requires supporting signals." />
              ) : (
                <div className="grid gap-5 md:grid-cols-2" data-testid="emerging-list">
                  {d.emerging.map((t) => (
                    <TrendCard key={t.topic} t={t} evidence={d.evidence} testid="emerging-card" />
                  ))}
                </div>
              )}
            </SectionCard>

            {/* ── Audience Intelligence ── */}
            <SectionCard icon={Users} title="Audience Intelligence"
              subtitle="Grounded in real audience comments where available"
              testid="section-audience">
              {d.audience.status !== "ok" || d.audience.items.length === 0 ? (
                <EmptyState icon={Users} testid="audience-empty"
                  title="Insufficient audience data"
                  description="Real audience signals come from public comment data on your niche." />
              ) : (
                <div className="grid gap-5 md:grid-cols-2" data-testid="audience-list">
                  {d.audience.items.map((a, i) => (
                    <AudienceCard key={i} a={a} evidence={d.evidence} />
                  ))}
                </div>
              )}
            </SectionCard>

            {/* ── What's working ── */}
            <SectionCard icon={LineChart} title="What's working for you"
              subtitle="Evidence-backed patterns from your own content"
              testid="section-content-patterns">
              {d.content.status === "no_connection" ? (
                <EmptyState icon={LineChart} testid="content-no-connection"
                  title="Connect a platform to analyze your existing content"
                  description="Performance patterns unlock once you connect a real platform."
                  action={<Button variant="outline" onClick={() => navigate("/connections")} className="rounded-full">Go to Connections</Button>} />
              ) : d.content.status === "insufficient_content" ? (
                <EmptyState icon={LineChart} testid="content-insufficient"
                  title="Publish more content to unlock performance patterns"
                  description="Not enough content data yet to detect reliable patterns." />
              ) : (
                <p className="text-sm text-muted-foreground">Not enough data yet.</p>
              )}
            </SectionCard>

            {/* ── Content Gaps ── */}
            <SectionCard icon={Target} title="Content Gaps"
              subtitle="High-opportunity areas with limited coverage in the available sources"
              testid="section-gaps">
              {d.gaps.length === 0 ? (
                <EmptyState icon={Target} testid="gaps-empty"
                  title="No content gaps detected"
                  description="No gaps are supported by the current evidence." />
              ) : (
                <div className="grid gap-5 md:grid-cols-2" data-testid="gaps-list">
                  {d.gaps.map((g, i) => (
                    <GapCard key={i} g={g} evidence={d.evidence} />
                  ))}
                </div>
              )}
            </SectionCard>
          </>
        )}
      </div>
    </AppShell>
  );
}
